import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FieldArray, type FormikProps } from 'formik';
import { forwardRef, useImperativeHandle, useMemo } from 'react';

import './SpecificationIntervalPopupContent.scss';

import { SelectField } from 'src/components/base/fields/SelectField';
import type { SpecificationIntervalPopupFormValues } from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalPopup.validation';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import type { ISelectOption } from 'src/types';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function MeasureUnitOptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

export type SpecificationIntervalPopupFormContentRef = {
  submit?: () => Promise<void>;
  isDisabled?: boolean;
};

type SpecificationIntervalPopupFormContentProps = {
  formikProps: FormikProps<SpecificationIntervalPopupFormValues>;

  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];
};

export const SpecificationIntervalPopupFormContent = forwardRef<
  SpecificationIntervalPopupFormContentRef,
  SpecificationIntervalPopupFormContentProps
>(({ formikProps: { values, isValid, submitForm }, multiselect, defaultMeasureUnit, allowedMeasureUnits }, ref) => {
  useImperativeHandle<SpecificationIntervalPopupFormContentRef, SpecificationIntervalPopupFormContentRef>(
    ref,
    () => ({
      submit: submitForm,
      isDisabled: !isValid,
    }),
    [submitForm, isValid]
  );

  const measureUnitsOptions: ISelectOption[] = useMemo(
    () =>
      allowedMeasureUnits.map((allowedMeasureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[allowedMeasureUnit],
        value: allowedMeasureUnit,
      })),
    [allowedMeasureUnits]
  );

  return (
    <div className="specification-interval-popup-content__container">
      <FieldArray
        name="values"
        render={(arrayHelpers) => {
          const showMeasureUnitsSelect = multiselect && measureUnitsOptions.length > 0;

          const gridLayoutItems = [
            '1fr', // Min Value
            '1fr', // Max Value,

            ...(showMeasureUnitsSelect ? ['70px'] : []), // Measure units
            ...(multiselect ? ['max-content'] : []), // Remove item button
          ].join(' ');

          return (
            <>
              {values.values.map((_, index) => (
                <div
                  key={String(index)}
                  className="specification-interval-popup-content__item"
                  style={{
                    gridTemplateColumns: gridLayoutItems,
                  }}
                >
                  <TextInputField
                    name={`values[${index}].minValue`}
                    type="number"
                    label="Min Value"
                    touchOnMount={true}
                    autoFocus={true}
                    addonAfter={
                      !multiselect &&
                      defaultMeasureUnit &&
                      defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                        <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                      )
                    }
                  />
                  <TextInputField
                    name={`values[${index}].maxValue`}
                    type="number"
                    label="Max Value"
                    touchOnMount={true}
                    addonAfter={
                      !multiselect &&
                      defaultMeasureUnit &&
                      defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                        <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                      )
                    }
                  />
                  {showMeasureUnitsSelect && (
                    <SelectField
                      className="specification-interval-popup-content__measure-units"
                      name={`values[${index}].measureUnit`}
                      options={measureUnitsOptions}
                      popupMatchSelectWidth={false}
                      touchOnMount={true}
                      optionRender={({ value }) => <MeasureUnitOptionRender value={value} />}
                      getPopupContainer={() =>
                        // Render popup in current element to get click events on dropdown inside `specification-interval-popup` element
                        (document.getElementsByClassName('specification-interval-popup')?.[0] as HTMLElement) ??
                        document.body
                      }
                    />
                  )}
                  {multiselect && (
                    <Button
                      className="specification-interval-popup-content__button"
                      title="Remove value"
                      type="primary"
                      danger={true}
                      size="middle"
                      icon={<MinusOutlined />}
                      disabled={values.values.length < 2}
                      onClick={arrayHelpers.handleRemove(index)}
                    />
                  )}
                </div>
              ))}
              {multiselect && (
                <Button
                  className="specification-interval-popup-content__button specification-interval-popup-content__add-button"
                  title="Add value"
                  type="primary"
                  size="middle"
                  icon={<PlusOutlined />}
                  onClick={arrayHelpers.handlePush({
                    measureUnit: defaultMeasureUnit,
                    minValue: null,
                    maxValue: null,
                  } as SpecificationIntervalPopupFormValues['values'][0])}
                />
              )}
            </>
          );
        }}
      />
    </div>
  );
});
